import { useAppSelector } from "@app/hooks"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import MsCalendar from "@masterschool/ui-calendar"
import { Typography } from "@mui/material"
import Stack from "@mui/material/Stack"
import { useMemo } from "react"
import { FetchEventSnackbar } from "../../../main/calendar/FetchEventSnackbar"
import { buildMsCalendarGroupingsAndFiltersForProgramOwner } from "../../../main/calendar/calendarMappers"
import { useEventActions } from "../../../main/calendar/useEventActions"
import { selectProgramsWithTracks } from "@features/program/programSliceSelectors"
import appTheme from "../../../theme/appTheme"

import { ProgramDto } from "@masterschool/course-builder-api"
import { useState } from "react"

import { selectLatestPublishedCoursesByIds } from "@features/coursesMenu/coursesSelectors"
import { selectTerms } from "@features/term/termSelector"
import { DateRange, defaultDateRange } from "@utils/date"
import {
  useGetCalendarById,
  useGetCalendarsForProgramOwner,
  useLazyPastPrograms,
  useLazyProgramClassCalendars,
} from "../../../main/calendar/useGetCalendar"
import { Role } from "@features/login/loginSlice"
import { useGetCourseClassCalendars } from "./programCalendarHooks"

export const ProgramCalendarView = ({ program }: { program: ProgramDto }) => {
  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange)
  // When on, the program calendar drops its single-program scope and shows ALL
  // the user's programs — reusing the main calendar's exact data path — so they
  // can invite across programs in place instead of creating duplicate events.
  const [showAllPrograms, setShowAllPrograms] = useState(false)
  // When on, each program node nests "Study halls" + per-course class groups;
  // off, classes sit flat under the program. Defaults on here — the course
  // structure is this view's reason to exist.
  const [groupByCourse, setGroupByCourse] = useState(true)

  const {
    staffCalendars,
    courseClassCalendars,
    programSchoolCalendars,
    courseDescriptors,
    terms,
  } = useGetCourseClassCalendars(program.id)

  const { calendars: allProgramsCalendars } =
    useGetCalendarsForProgramOwner(showAllPrograms)
  const programs = useAppSelector(selectProgramsWithTracks)
  // When "Invite across all programs" is on, eager-load every running program's
  // classes (same chunked path as the main calendar) so the invite picker shows
  // class variants without expanding each program. Off, this is empty and the
  // single-program scope (useGetCourseClassCalendars above) supplies classes.
  const allProgramsRunningIds = useMemo(
    () =>
      showAllPrograms
        ? [
            ...new Set(
              allProgramsCalendars.programSchoolCalendars.map(
                (c) => c.offeringDetails.programId,
              ),
            ),
          ]
        : [],
    [showAllPrograms, allProgramsCalendars],
  )
  const { classCalendarsByProgram, loadProgramClasses } =
    useLazyProgramClassCalendars(allProgramsRunningIds)

  // Course descriptors + terms for the all-programs scope: derived from the
  // classes actually loaded so far (courses/terms are app-wide redux data).
  const loadedClassCourseIds = useMemo(
    () => [
      ...new Set(
        Object.values(classCalendarsByProgram).flatMap((state) =>
          state.calendars.map((c) => c.courseClassDetails.courseId),
        ),
      ),
    ],
    [classCalendarsByProgram],
  )
  const allProgramsCourseDescriptors = useAppSelector((state) =>
    selectLatestPublishedCoursesByIds(state, loadedClassCourseIds),
  )
  const allTerms = useAppSelector(selectTerms)

  const { groupings, customFilters, flatCalendarsForMenu } = useMemo(() => {
    if (showAllPrograms) {
      const allProgramsView = buildMsCalendarGroupingsAndFiltersForProgramOwner(
        allProgramsCalendars,
        programs,
        { classCalendarsByProgram, loadProgramClasses },
        groupByCourse
          ? { courseDescriptors: allProgramsCourseDescriptors, terms: allTerms }
          : undefined,
      )
      return {
        groupings: allProgramsView.groupings,
        customFilters: allProgramsView.customFilters,
        flatCalendarsForMenu: [
          ...allProgramsCalendars.programSchoolCalendars,
          ...allProgramsCalendars.programStaffCalendars,
          ...allProgramsCalendars.studentCalendars,
        ],
      }
    }
    // Single-program scope: the SAME mapper as the main calendar, fed one
    // program. Classes are passed pre-loaded through the lazy-state shape so
    // both scopes exercise one code path; the program node's checkbox selects
    // study halls + every class (its whole subtree).
    //
    // Staff go in as `allMasterschoolStaffCalendars` (invite-only) — the
    // original single-program menu listed them only in the invite picker, not
    // as selectable view calendars, so this preserves that: you can still
    // invite a tutor without instructors cluttering (or auto-selecting into)
    // the program's own grid.
    const singleProgramView = buildMsCalendarGroupingsAndFiltersForProgramOwner(
      {
        programSchoolCalendars,
        programStaffCalendars: [],
        studentCalendars: [],
        allMasterschoolStaffCalendars: staffCalendars,
      },
      programs,
      {
        classCalendarsByProgram: {
          [program.id]: { isLoading: false, calendars: courseClassCalendars },
        },
        loadProgramClasses: () => undefined,
      },
      groupByCourse ? { courseDescriptors, terms } : undefined,
    )
    return {
      groupings: singleProgramView.groupings,
      customFilters: singleProgramView.customFilters,
      flatCalendarsForMenu: [
        ...programSchoolCalendars,
        ...courseClassCalendars,
        ...staffCalendars,
      ],
    }
  }, [
    showAllPrograms,
    groupByCourse,
    allProgramsCalendars,
    programs,
    classCalendarsByProgram,
    loadProgramClasses,
    allProgramsCourseDescriptors,
    allTerms,
    staffCalendars,
    programSchoolCalendars,
    courseClassCalendars,
    courseDescriptors,
    terms,
    program.id,
  ])

  const user = useAppSelector(selectLoggedInUser)
  const isAdmin =
    user?.roles.some((r) =>
      [
        Role.SchoolMasterAdmin,
        Role.LmsAdmin,
        Role.MasterschoolEmployee,
      ].includes(r),
    ) ?? false
  // Past programs only make sense once the user has opted into the all-programs
  // view; in single-program scope there's nothing past to fold in.
  const lazyPastPrograms = useLazyPastPrograms(showAllPrograms && isAdmin)

  const {
    msEvents,
    onChange,
    onDelete,
    addCalendarIdsToFetch,
    setFetchOrganized,
    loading,
    onCalendarDatesChange,
    onMergedCopySave,
  } = useEventActions([], dateRange, setDateRange)

  const getCalendarById = useGetCalendarById(msEvents, flatCalendarsForMenu)

  if (!user) {
    return null
  }

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        bgColor: appTheme.palette.primary.contrast,
      }}
    >
      <Typography variant="h6" pl="24px" pb="8px">
        Calendar
      </Typography>
      {loading && <FetchEventSnackbar />}
      <Stack
        flex={1}
        overflow="auto"
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <MsCalendar
          // Scope the selection to this program (and remount when it changes) so
          // moving between cohorts starts from this program's own classes instead
          // of accumulating the previous cohort's, and never touches the main
          // calendar's saved selection.
          key={program.id}
          selectionScopeKey={program.id}
          events={msEvents}
          calendars={groupings}
          lazySection={lazyPastPrograms}
          customCalendarFilters={customFilters}
          onMergedCopySave={onMergedCopySave}
          getCalendar={getCalendarById}
          onEventChange={onChange}
          userId={user.clientId}
          onDeleteEvent={onDelete}
          canJoinLiveSessions={false}
          showDraftEvents
          showOrganizedByMe
          // Land on this program fully populated: every group open and every
          // class/study-hall pre-selected. Only in single-program scope —
          // expanding/selecting all programs at once would be a wall of events.
          defaultExpandGroups={!showAllPrograms}
          defaultSelectAllCalendars={!showAllPrograms}
          menuActions={[
            {
              label: showAllPrograms
                ? "Showing all programs"
                : "Invite across all programs",
              isActive: showAllPrograms,
              helperText: showAllPrograms
                ? "Tap to return to this program only"
                : "This calendar shows only this program",
              onClick: () => setShowAllPrograms((value) => !value),
            },
            {
              label: "Group by course",
              isActive: groupByCourse,
              helperText: groupByCourse
                ? "Classes nested under Study halls & course groups"
                : "Classes listed flat under each program",
              onClick: () => setGroupByCourse((value) => !value),
            },
          ]}
          onSelectedCalendarChange={(calendarIds) => {
            setFetchOrganized(calendarIds.includes("organized"))
            const realCalendarIds = calendarIds.filter(
              (c) => !c.includes("draft") && !c.includes("organized"),
            )
            addCalendarIdsToFetch(realCalendarIds)
          }}
          supportCreateEventTypes={["live-session", "regular"]}
          onDatesRangeChange={onCalendarDatesChange}
        />
      </Stack>
    </Stack>
  )
}
